import {
  FILTER_PARAM_NAMES,
  clinicianSearchClearQueryParams,
} from 'client-portal/utils/clinicianSearch/facets/manifest';
import { PRESCREENER_RESET_OBJECT } from 'client-portal/utils/prescreener/constants';
import { STEP_IDS } from 'client-portal/services/widget-steps';
import { action, set } from '@ember/object';
import { bookingPreselectionQueryParamsFromClinicianSearch } from 'client-portal/utils/clinicianSearch/preselection';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import { waitForNext } from 'ember-simplepractice/utils/waiters';
import ENV from 'client-portal/config/environment';
import RequestBaseRoute from './base';
import uuidv4 from 'uuid/v4';

export default class RequestClinicianSearch extends RequestBaseRoute {
  @service store;
  @service request;
  @service clinicianSearch;
  @service pendingAppointment;
  @service router;

  templateName = '-request/clinician-search';
  stepId = STEP_IDS.SELECT_CLINICIAN_SEARCH;
  _hasEnteredStep = false;

  beforeModel() {
    if (!this.clinicianSearch.isEnabled) {
      this.request.replaceWith(this, 'clinician');
    }
  }

  setupController(controller, model) {
    super.setupController(controller, model);
    set(controller, 'nextStep', this.nextStep.bind(this));
  }

  resetController(controller, isExiting) {
    super.resetController(controller, isExiting);
    if (isExiting) {
      this._hasEnteredStep = false;
      controller.resetQueryParams();
    }
  }

  resolvedGlobalMonarchChannelId(params, reservation) {
    return (
      String(params.globalMonarchChannelId ?? '').trim() ||
      String(reservation?.get('channel.id') ?? '').trim() ||
      undefined
    );
  }

  queryParams = {
    pageNumber: { refreshModel: true },
    globalMonarchChannelId: { refreshModel: true },
    ...Object.fromEntries(FILTER_PARAM_NAMES.map(n => [n, { refreshModel: true }])),
  };

  @(task(function* (pageNumber) {
    if (!this.clinicianSearch.isEnabled) return;

    try {
      yield this.clinicianSearch.sendAll.ensureLoaded();
    } catch {
      return;
    }

    let routeParams = {
      ...this.clinicianSearch.query.toRouteParams(),
      pageNumber,
    };
    let filteredCohort = this.clinicianSearch.sendAll.filterForRouteParams(routeParams);

    // Adaptive enrichment for the full filtered cohort (batched, capped).
    // Fire-and-forget — the controller re-sorts and re-paginates as batches
    // land via nextSlots.enrichmentVersion. Pass pageNumber so deep links
    // (e.g. ?pageNumber=5) extend the enrichment window past the default
    // "first two pages" target instead of stranding later cards in the
    // skeleton state.
    this.clinicianSearch.clinicianDetails.enrichCohort(filteredCohort, { pageNumber });
    this.clinicianSearch.nextSlots.enrichCohort(
      filteredCohort,
      {
        officeIds: [],
        cptCodeRateId: null,
        weekdays: [],
        timeBuckets: [],
      },
      { pageNumber }
    );
  }).restartable())
  loadCatalogTask;

  async model(params, transition) {
    let reservation = this.modelFor(this.request.baseRouteName);
    let urlParams = transition?.to?.queryParams ?? {};

    if (urlParams.preselected || urlParams.clinicianId) {
      await this.resetPreselection();
      return;
    }

    let pageSize = ENV.clinicianSearchPageSize;
    let pageNumber = parseInt(String(params.pageNumber ?? 1), 10);
    if (Number.isNaN(pageNumber) || pageNumber < 1) {
      pageNumber = 1;
    }

    this.clinicianSearch.query.syncFromRouteParams({
      ...params,
      globalMonarchChannelId: this.resolvedGlobalMonarchChannelId(params, reservation),
    });

    this.loadCatalogTask.perform(pageNumber);
    await waitForNext();

    return {
      pageSize,
    };
  }

  async resetPreselection() {
    await this.router.replaceWith({
      queryParams: clinicianSearchClearQueryParams(),
    });
  }

  afterModel() {
    super.afterModel(...arguments);
    this.pendingAppointment.reset();
  }

  @action
  async nextStep(clinician, transitionParams) {
    let reservation = this.modelFor(this.request.baseRouteName);
    if (!reservation) {
      return;
    }

    let queryParams = bookingPreselectionQueryParamsFromClinicianSearch({
      clinicianId: clinician.id,
      officeIds: this.clinicianSearch.query.officeIds,
      cptCodes: this.clinicianSearch.query.cptCodes,
      availableDays: this.clinicianSearch.query.availableDays,
      availableTimes: this.clinicianSearch.query.availableTimes,
      queryParams: transitionParams?.queryParams,
    });
    queryParams = this.clinicianSearch.preselection.start({
      reservation,
      clinicianId: clinician.id,
      queryParams,
    });

    let navParams = { ...(transitionParams ?? {}), queryParams };

    super.nextStep('clinician', clinician, 'service', navParams);

    let controller = this.controllerFor(this.request.baseRouteName);
    controller.updateNavigationSteps();
  }

  @action
  didTransition() {
    let reservation = this.modelFor(this.request.baseRouteName);
    if (!reservation || this._hasEnteredStep) {
      return;
    }

    this._hasEnteredStep = true;

    reservation.setProperties({
      clinician: null,
      cptCode: null,
      office: null,
      startTime: null,
      clientType: null,
      message: null,
      hasCompletedMessageStep: false,
      channelFields: null,
      channelUploads: null,
      channelUploadsComplete: null,
      hasCompletedInformationStep: false,
      ...PRESCREENER_RESET_OBJECT,
      flowId: uuidv4(),
    });

    this._trackAppointmentStep(reservation);
    return true;
  }
}
